import type { PlanarGraph, PlanarGraphLoops } from "geom/planarGraph";
import { planarGraphAdjacentHalfEdge, planarGraphHalfEdgeNormal, planarGraphHalfEdgeTangent } from "geom/planarGraph";
import { vec2Dot, vec2NormsCollinear } from "geom/vec2";

const INSIDE_CORNER_EPSILON = 1e-4;

interface GetInsideCornerHalfEdgesOptions {
  planarGraph: PlanarGraph;
  coveredPerimeters: PlanarGraphLoops;
}

export function getInsideCornerHalfEdges({
  planarGraph,
  coveredPerimeters,
}: GetInsideCornerHalfEdgesOptions): number[] {
  const insideCornerHalfEdges: { [id: number]: boolean } = {};
  const perimetersWithoutExteriorFaces = coveredPerimeters;

  // loops through the exterior half edges of each exterior perimeter in a home.
  for (let i = 0, l = perimetersWithoutExteriorFaces.faceVertices.length; i < l; i++) {
    const perimeterHalfEdges = perimetersWithoutExteriorFaces.faceHalfEdges[i];
    const perimeterHalfEdgeCount = perimeterHalfEdges.length;

    const fwdCornerIndices: number[] = [];
    const revCornerIndices: number[] = [];

    for (let j = perimeterHalfEdgeCount - 1; j >= 0; j--) {
      const nextJ = j > 0 ? j - 1 : perimeterHalfEdgeCount - 1;

      const halfEdge = perimeterHalfEdges[j];
      const nextHalfEdge = perimeterHalfEdges[nextJ];

      const [edgeNormalX, edgeNormalY] = planarGraphHalfEdgeNormal(planarGraph, halfEdge);
      const [nextEdgeDirX, nextEdgeDirY] = planarGraphHalfEdgeTangent(planarGraph, nextHalfEdge);

      const endDot = vec2Dot(edgeNormalX, edgeNormalY, nextEdgeDirX, nextEdgeDirY);

      // ignore if not inside corner
      if (endDot <= INSIDE_CORNER_EPSILON) {
        continue;
      }

      revCornerIndices.push(j);
      fwdCornerIndices.push(nextJ);

      //these two half edges are inside corners
      insideCornerHalfEdges[halfEdge] = true;
      insideCornerHalfEdges[planarGraphAdjacentHalfEdge(halfEdge)] = true;
      insideCornerHalfEdges[nextHalfEdge] = true;
      insideCornerHalfEdges[planarGraphAdjacentHalfEdge(nextHalfEdge)] = true;
    }

    // add collinear edges going forward from first inside corner edge
    for (const index of fwdCornerIndices) {
      for (let j = perimeterHalfEdgeCount - 1; j >= 0; j--) {
        const currJ = (j + 1 + index) % perimeterHalfEdgeCount;
        const nextJ = currJ > 0 ? currJ - 1 : perimeterHalfEdgeCount - 1;

        const isCollinear = addInsideCornerHalfEdgeIfCollinear(
          planarGraph,
          perimeterHalfEdges,
          currJ,
          nextJ,
          insideCornerHalfEdges,
        );
        if (!isCollinear) {
          break;
        }
      }
    }

    // add collinear edges going reverse from first inside corner edge
    for (const index of revCornerIndices) {
      for (let j = 0; j < perimeterHalfEdgeCount; j++) {
        const currJ = (j + index) % perimeterHalfEdgeCount;
        const nextJ = (j + 1 + index) % perimeterHalfEdgeCount;

        const isCollinear = addInsideCornerHalfEdgeIfCollinear(
          planarGraph,
          perimeterHalfEdges,
          currJ,
          nextJ,
          insideCornerHalfEdges,
        );
        if (!isCollinear) {
          break;
        }
      }
    }
  }

  return Object.keys(insideCornerHalfEdges).map((x) => parseInt(x));
}

function addInsideCornerHalfEdgeIfCollinear(
  planarGraph: PlanarGraph,
  perimeterHalfEdges: number[],
  currJ: number,
  nextJ: number,
  insideCornerHalfEdges: { [id: number]: boolean },
): boolean {
  const halfEdge = perimeterHalfEdges[currJ];
  const nextHalfEdge = perimeterHalfEdges[nextJ];

  const [edgeDirX, edgeDirY] = planarGraphHalfEdgeTangent(planarGraph, halfEdge);
  const [nextEdgeDirX, nextEdgeDirY] = planarGraphHalfEdgeTangent(planarGraph, nextHalfEdge);

  if (vec2NormsCollinear(edgeDirX, edgeDirY, nextEdgeDirX, nextEdgeDirY)) {
    insideCornerHalfEdges[nextHalfEdge] = true;
    insideCornerHalfEdges[planarGraphAdjacentHalfEdge(nextHalfEdge)] = true;
    return true;
  }

  // not collinear
  return false;
}
